import { toast } from "react-toastify";
import { TOAST_TIME_OUT } from "../constants/constants";
import moment from "moment";
import momentTimeZone from "moment-timezone";
import storage from "./storage";
import { get, isEmpty } from "lodash";
import { parsePhoneNumberFromString } from "libphonenumber-js";

export const logger = (tag, data) => {
  if (process.env.NODE_ENV === "development") {
    console.log(`${tag} :: ${data}`);
  }
};

// One wording for "a required field is empty" on every form. Other validation problems
// (invalid value, wrong date order, ...) keep their own specific message.
export const REQUIRED_FIELDS_MSG = "Please fill in all required fields";

const isRequiredFieldMessage = (message) => {
  const text = String(message || "");
  return /required/i.test(text) || (/^please (enter|select|choose)\b/i.test(text) && !/valid/i.test(text));
};

// Message for the validation notification, from a form's { field: message } errors
export const getValidationToastMessage = (errors) => {
  const messages = Object.values(errors || {}).filter(Boolean);
  return messages.some(isRequiredFieldMessage) ? REQUIRED_FIELDS_MSG : messages[0];
};

export const customToast = (message, type = "success") => {
  if (!message) return;
  const msg = typeof message === "string" ? message : (message?.message || String(message));
  const toastType = typeof type === "string" ? type.toLowerCase() : "success";

  if (toastType === "error") {
    toast.error(msg, {
      autoClose: TOAST_TIME_OUT,
    });
  } else if (toastType === "warn" || toastType === "warning") {
    toast.warn(msg, {
      autoClose: TOAST_TIME_OUT,
    });
  } else if (toastType === "info") {
    if (typeof toast.info === "function") {
      toast.info(msg, {
        autoClose: TOAST_TIME_OUT,
      });
    } else {
      toast(msg, {
        autoClose: TOAST_TIME_OUT,
      });
    }
  } else {
    toast.success(msg, {
      autoClose: TOAST_TIME_OUT,
    });
  }
};

customToast.success = (message) => {
  if (!message) return;
  const msg = typeof message === "string" ? message : (message?.message || String(message));
  toast.success(msg, {
    autoClose: TOAST_TIME_OUT,
  });
};

customToast.error = (message) => {
  if (!message) return;
  const msg = typeof message === "string" ? message : (message?.message || String(message));
  toast.error(msg, {
    autoClose: TOAST_TIME_OUT,
  });
};

customToast.warn = (message) => {
  if (!message) return;
  const msg = typeof message === "string" ? message : (message?.message || String(message));
  toast.warn(msg, {
    autoClose: TOAST_TIME_OUT,
  });
};

customToast.warning = customToast.warn;

customToast.info = (message) => {
  if (!message) return;
  const msg = typeof message === "string" ? message : (message?.message || String(message));
  if (typeof toast.info === "function") {
    toast.info(msg, {
      autoClose: TOAST_TIME_OUT,
    });
  } else {
    toast(msg, {
      autoClose: TOAST_TIME_OUT,
    });
  }
};


export const formatInternationalPhoneNumber = (phoneNumber) => {
  // Remove all non-numeric characters from the phone number
  const cleaned = ("" + phoneNumber).replace(/\D/g, "");

  // If it starts with '1' (US/Dominica/etc.)
  if (cleaned.startsWith("1")) {
    if (cleaned.length === 11) {
      const match = cleaned.match(/^1(\d{3})(\d{3})(\d{4})$/);
      if (match) {
        return `+1 (${match[1]}) ${match[2]}-${match[3]}`;
      }
    } else if (cleaned.length === 12) {
      const match = cleaned.match(/^1(\d{3})(\d{3})(\d{4})(\d{1})$/);
      if (match) {
        return `+1 (${match[1]}) ${match[2]}-${match[3]} ${match[4]}`;
      }
    }
  }

  // Match the cleaned number to the desired format for other cases
  const match = cleaned.match(/^(\d{1,3})(\d{3})(\d{3})(\d{4})$/);

  if (match) {
    // Format the phone number as "+XXX (XXX) XXX-XXXX"
    return "+" + match[1] + " (" + match[2] + ") " + match[3] + "-" + match[4];
  }

  // Return the original input if it doesn't match the expected format
  return phoneNumber;
};

export const handleCopy = (text) => {
  navigator.clipboard
    .writeText(text)
    .then(() => {
      customToast.success("Text copied!");
    })
    .catch((err) => {
      customToast.error("Failed to copy!");
    });
};

// Convert date to desired format
export const formattedDate = (date) => {
  if (!date) return "";
  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date.trim())) {
    return date.trim();
  }
  const m = moment(date, [
    "DD MMM YYYY",
    "DD-MMM-YYYY",
    "DD/MMM/YYYY",
    "DD-MM-YYYY",
    "DD/MM/YYYY",
    "YYYY-MM-DD",
    "YYYYMMDD",
    "MM-DD-YYYY",
    "MM/DD/YYYY",
    moment.ISO_8601,
  ]);
  return m.isValid() ? m.format("YYYY-MM-DD") : moment(date).format("YYYY-MM-DD");
};

export const formattedTimeForLocal = (time) =>
  moment(time, "HH:mm:ss").format("HH:mm:ss");

export const combineAndFormatDateAndTimeToUTC = (date, time) => moment(date + " " + time).utc().format("YYYY-MM-DD HH:mm:ss")

// Convert time to desired format
export const formattedTime = (time) =>
  moment(time, "HH:mm:ss").utc().format("HH:mm:ss");

// Convert date and time to desired format
export const formatted24DateAndTime = (time) => {
  if (!time) return "";
  if (moment.isMoment(time)) return time.utc().format("YYYY-MM-DD HH:mm:ss");
  const m = moment.utc(time, [
    "YYYY-MM-DD hh:mm:ss A",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD",
    "MM-DD-YYYY hh:mm:ss A",
    "MM-DD-YYYY HH:mm:ss",
    "MM-DD-YYYY HH:mm",
    "MM-DD-YYYY",
    moment.ISO_8601,
  ]);
  if (m.isValid()) {
    return m.format("YYYY-MM-DD HH:mm:ss");
  }
  const fallback = moment(time);
  return fallback.isValid() ? fallback.utc().format("YYYY-MM-DD HH:mm:ss") : "";
};

// Convert date and time to desired format
export const formattedDateAndTime = (time) => {
  if (!time) return "";
  if (moment.isMoment(time)) return time.utc().format("YYYY-MM-DD HH:mm:ss");
  const m = moment.utc(time, [
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD hh:mm:ss A",
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD",
    "MM-DD-YYYY hh:mm:ss A",
    "MM-DD-YYYY HH:mm:ss",
    "MM-DD-YYYY HH:mm",
    "MM-DD-YYYY",
    moment.ISO_8601,
  ]);
  if (m.isValid()) {
    return m.format("YYYY-MM-DD HH:mm:ss");
  }
  const fallback = moment(time);
  return fallback.isValid() ? fallback.utc().format("YYYY-MM-DD HH:mm:ss") : "";
};

// Convert Date UTC to Local timezone
export const formattedDateUTCtoLocal = (date, format = "DD MMM YYYY") => {
  if (!date) return "";
  let targetFormat = format;
  if (format === "DD-MM-YYYY" || format === "DD/MM/YYYY" || !format) {
    targetFormat = "DD MMM YYYY";
  }

  if (targetFormat === "DD MMM YYYY") {
    if (typeof date === "string") {
      const trimmed = date.trim();
      if (/^\d{2}\s+[A-Za-z]{3}\s+\d{4}$/.test(trimmed)) {
        return trimmed;
      }
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed.slice(0, 10)) && trimmed.length === 10) {
        const m = moment(trimmed, "YYYY-MM-DD");
        if (m.isValid()) return m.format("DD MMM YYYY");
      }
      if (/^\d{2}-\d{2}-\d{4}$/.test(trimmed)) {
        const m = moment(trimmed, "DD-MM-YYYY");
        if (m.isValid()) return m.format("DD MMM YYYY");
      }
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(trimmed)) {
        const m = moment(trimmed, "DD/MM/YYYY");
        if (m.isValid()) return m.format("DD MMM YYYY");
      }
    }
  } else if (targetFormat === "YYYY-MM-DD") {
    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date.trim().slice(0, 10)) && date.trim().length === 10) {
      return date.trim();
    }
    if (typeof date === "string" && /^\d{2}-\d{2}-\d{4}$/.test(date.trim())) {
      const parts = date.trim().split("-");
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
    if (typeof date === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(date.trim())) {
      const parts = date.trim().split("/");
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }
  } else if (targetFormat === "MM-DD-YYYY") {
    if (typeof date === "string" && /^\d{2}-\d{2}-\d{4}$/.test(date)) {
      return date;
    }
    if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}$/.test(date.trim().slice(0, 10)) && date.trim().length === 10) {
      const parts = date.trim().split("-");
      return `${parts[1]}-${parts[2]}-${parts[0]}`;
    }
  }
  const m = moment.utc(date, [
    "DD MMM YYYY",
    "DD-MMM-YYYY",
    "DD-MM-YYYY",
    "DD/MM/YYYY",
    "YYYY-MM-DDTHH:mm:ss.SSSSSSZ",
    "YYYY-MM-DDTHH:mm:ss.SSSZ",
    "YYYY-MM-DDTHH:mm:ss.SSSSSS",
    "YYYY-MM-DDTHH:mm:ss.SSS",
    "YYYY-MM-DDTHH:mm:ss",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD",
    "MM-DD-YYYY",
    "MM/DD/YYYY",
    moment.ISO_8601,
  ]);
  if (m.isValid()) {
    return m.local().format(targetFormat);
  }
  const fallback = moment(date);
  return fallback.isValid() ? fallback.local().format(targetFormat) : String(date);
};

// Convert Time UTC to Local timezone
export const formattedTimeUTCtoLocal = (time) =>
  moment.utc(time, "HH:mm:ss").local().format("hh:mm:ss A");

export const formattedTimeUTCtoLocalWithoutSec = (time) =>
  moment.utc(time, "HH:mm:ss").local().format("hh:mm A");

// Convert Date and Time UTC to Local timezone
export const formattedDateAndTimeUTCtoLocal = (
  date,
  format = "DD MMM YYYY hh:mm:ss A",
) => {
  if (!date) return "";
  let targetFormat = format;
  if (format === "DD-MM-YYYY hh:mm:ss A") {
    targetFormat = "DD MMM YYYY hh:mm:ss A";
  } else if (format === "DD-MM-YYYY hh:mm A") {
    targetFormat = "DD MMM YYYY hh:mm A";
  } else if (format === "DD-MM-YYYY HH:mm:ss") {
    targetFormat = "DD MMM YYYY HH:mm:ss";
  } else if (format === "DD-MM-YYYY HH:mm") {
    targetFormat = "DD MMM YYYY HH:mm";
  } else if (format === "DD-MM-YYYY") {
    targetFormat = "DD MMM YYYY";
  }
  const m = moment.utc(date, [
    "DD MMM YYYY hh:mm:ss A",
    "DD MMM YYYY hh:mm A",
    "DD MMM YYYY HH:mm:ss",
    "DD MMM YYYY HH:mm",
    "DD MMM YYYY",
    "YYYY-MM-DDTHH:mm:ss.SSSSSSZ",
    "YYYY-MM-DDTHH:mm:ss.SSSZ",
    "YYYY-MM-DDTHH:mm:ss.SSSSSS",
    "YYYY-MM-DDTHH:mm:ss.SSS",
    "YYYY-MM-DDTHH:mm:ss",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD HH:mm",
    "YYYY-MM-DD",
    "DD-MM-YYYY hh:mm:ss A",
    "DD-MM-YYYY HH:mm:ss",
    "DD-MM-YYYY HH:mm",
    "DD-MM-YYYY",
    "MM-DD-YYYY hh:mm:ss A",
    "MM-DD-YYYY HH:mm:ss",
    "MM-DD-YYYY",
    moment.ISO_8601,
  ]);
  if (m.isValid()) {
    return m.local().format(targetFormat);
  }
  const fallback = moment(date);
  return fallback.isValid() ? fallback.local().format(targetFormat) : "";
};

export const getUserEmail = () => {
  const userJson = JSON.parse(storage.get("user"));
  return get(userJson, "email", "");
};

// Convert time to HH:mm AM/PM format
export const formatTimeTo12Hour = (time) =>
  moment.utc(time, "HH:mm:ss").local().format("hh:mm A");

// Convert time to Date and Time Object
export const formatDateTime = (time) => moment(time);

export const parseDateForStartEndValidation = (value, originalValue) => {
  if (!originalValue) return null;
  if (moment.isMoment(originalValue)) {
    return originalValue.toDate();
  }
  if (originalValue instanceof Date) {
    return originalValue;
  }
  const parsedDate = moment(originalValue, [
    "DD MMM YYYY",
    "DD-MMM-YYYY",
    "DD-MM-YYYY",
    "YYYY-MM-DD",
    "MM-DD-YYYY",
    "MM/DD/YYYY",
    "YYYY/MM/DD",
    "YYYY-MM-DDTHH:mm:ss.SSSZ",
    "YYYY-MM-DD HH:mm:ss",
    "MM-DD-YYYY HH:mm:ss",
    "MM-DD-YYYY hh:mm A",
  ]);
  return parsedDate.isValid() ? parsedDate.toDate() : null;
};

export const parseDateTimeForValidation = (value, originalValue) => {
  if (!originalValue) return null;
  if (moment.isMoment(originalValue)) {
    return originalValue.toDate();
  }
  if (originalValue instanceof Date) {
    return originalValue;
  }
  const parsedDate = moment(
    originalValue,
    [
      "DD MMM YYYY hh:mm:ss A",
      "DD MMM YYYY hh:mm A",
      "DD MMM YYYY HH:mm:ss",
      "DD MMM YYYY HH:mm",
      "DD MMM YYYY",
      "DD-MMM-YYYY",
      "DD-MM-YYYY hh:mm:ss A",
      "DD-MM-YYYY HH:mm:ss",
      "DD-MM-YYYY HH:mm",
      "DD-MM-YYYY",
      "YYYY-MM-DD hh:mm:ss A",
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DD",
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DDTHH:mm:ss.SSSZ",
      "MM-DD-YYYY hh:mm:ss A",
      "MM-DD-YYYY hh:mm A",
      "MM-DD-YYYY HH:mm",
      "MM-DD-YYYY",
    ],
    true,
  );
  if (parsedDate.isValid()) return parsedDate.toDate();
  const fallback = moment(originalValue);
  return fallback.isValid() ? fallback.toDate() : null;
};

export const getLocalTimeZone = () => {
  const timezone = momentTimeZone.tz.guess();
  return momentTimeZone.tz(timezone).format("z");
};

export function parseTimeString(timeString) {
  if (typeof timeString === "object") {
    return timeString;
  }

  const date = new Date(timeString);

  if (isNaN(date.getTime())) {
    throw new Error("Invalid time string format");
  }

  return moment(date).format("HH:mm:ss");
}

export const parseMobileNumberValidation = (value) => {
  if (value && value.includes(",")) {
    const num = value.split(",");
    return `+${num[0]}${num[1]}`;
  } else if (value && value.includes("+")) {
    return value;
  }
  return "";
};

/* ---------- Phone field (new UI) ----------
   PhoneInput values are "dialCode,number" (e.g. "1767,2551234"), the same format as
   the old CustomPhoneInput; values loaded from the API are "+17672551234". */

// True when no digits were entered after the country code
export const isPhoneEmpty = (value) => {
  if (!value) return true;
  const str = String(value);
  if (str.includes(",")) return !str.split(",")[1].replace(/\D/g, "");
  return !str.replace(/\D/g, "");
};

// Same rule as the old UI forms: Dominica (+1767) accepts 7 or 8 local digits,
// every other number must be valid for its country
export const isPhoneNumberValid = (value) => {
  if (isPhoneEmpty(value)) return false;
  const str = String(value).trim();
  let fullNumber = "";
  if (str.includes(",")) {
    const parts = str.split(",");
    fullNumber = `+${parts[0]}${parts[1]}`;
  } else {
    fullNumber = str.startsWith("+") ? str : `+${str}`;
  }
  fullNumber = fullNumber.replace(/[^\d+]/g, "");
  if (fullNumber.startsWith("+1767")) {
    const localPart = fullNumber.slice(5);
    if (localPart.length === 7 || localPart.length === 8) {
      return true;
    }
  }
  try {
    const phoneNumber = parsePhoneNumberFromString(fullNumber);
    return phoneNumber ? phoneNumber.isValid() : false;
  } catch (error) {
    return false;
  }
};

// Error message for a phone field, or "" when it is fine
export const getPhoneError = (value, { required = true } = {}) => {
  if (isPhoneEmpty(value)) return required ? "Phone Number is required" : "";
  return isPhoneNumberValid(value) ? "" : "Phone number is invalid";
};

// Payload value: "+17672551234", or "" when nothing was entered
export const toPhonePayload = (value) => {
  if (isPhoneEmpty(value)) return "";
  const str = String(value).trim();
  if (str.includes(",")) {
    return parseMobileNumberValidation(str).replace(/[^\d+]/g, "");
  }
  // Loaded from the API and not edited: "+17672551234", or plain digits when the
  // API stores the number as an integer (e.g. suppliers return 17672551234)
  return `+${str.replace(/\D/g, "")}`;
};

export const validatePhoneNumber = (val) => {
  if (!val) return true;
  let fullNumber = "";
  let parts = [];
  if (val.includes(",")) {
    parts = val.split(",");
    fullNumber = `+${parts[0]}${parts[1]}`;
  } else {
    fullNumber = val.startsWith("+") ? val : `+${val}`;
  }

  const isDominica =
    (val.includes(",") && (parts[0] === "1767" || parts[0] === "767")) ||
    fullNumber.startsWith("+1767") ||
    fullNumber.startsWith("+767");

  if (isDominica) {
    let dominicaNum = "";
    if (val.includes(",")) {
      if (parts[0] === "1767" || parts[0] === "767") {
        dominicaNum = parts[1];
      } else if (parts[0] === "1" && parts[1].startsWith("767")) {
        dominicaNum = parts[1].slice(3);
      } else {
        dominicaNum = parts[1];
      }
    } else if (fullNumber.startsWith("+1767")) {
      dominicaNum = fullNumber.slice(5);
    } else if (fullNumber.startsWith("+767")) {
      dominicaNum = fullNumber.slice(4);
    }
    return /^\d{8}$/.test(dominicaNum);
  }

  try {
    const phoneNumber = parsePhoneNumberFromString(fullNumber);
    if (phoneNumber && phoneNumber.country === "DM") {
      return /^\d{8}$/.test(phoneNumber.nationalNumber);
    }
    return phoneNumber ? phoneNumber.isValid() : false;
  } catch (error) {
    return false;
  }
};

export const sensitizeEmptyData = (value) => {
  return isEmpty(`${value}`) ? null : value;
};

const isFormattedTime = (time) =>
  moment(time, ["hh:mm:ss A", "HH:mm:ss"], true).isValid();

export const extractTime = (dateTime) => {
  if (!dateTime) return "";
  const m = moment(dateTime, [
    "DD MMM YYYY hh:mm:ss A",
    "DD MMM YYYY HH:mm:ss",
    "DD-MM-YYYY hh:mm:ss A",
    "DD-MM-YYYY HH:mm:ss",
    "YYYY-MM-DD hh:mm:ss A",
    "YYYY-MM-DD HH:mm:ss",
    "HH:mm:ss",
    "hh:mm:ss A",
    "hh:mm A",
    moment.ISO_8601,
  ]);
  return m.isValid() ? m.format("HH:mm:ss") : moment(dateTime).format("HH:mm:ss");
};

export const extractDate = (dateTime) => {
  if (!dateTime) return "";
  const m = moment(dateTime, [
    "DD MMM YYYY hh:mm:ss A",
    "DD MMM YYYY HH:mm:ss",
    "DD MMM YYYY",
    "DD-MM-YYYY hh:mm:ss A",
    "DD-MM-YYYY HH:mm:ss",
    "DD-MM-YYYY",
    "YYYY-MM-DD hh:mm:ss A",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD",
    "MM-DD-YYYY hh:mm:ss A",
    moment.ISO_8601,
  ]);
  return m.isValid() ? m.format("YYYY-MM-DD") : moment(dateTime).format("YYYY-MM-DD");
};

export const formattedHeartsTimeUTCtoLocal = (
  totalSeconds,
  spiritLogDate,
  spiritLogTime,
) => {
  const timeString = isFormattedTime(spiritLogTime)
    ? moment(spiritLogTime, ["hh:mm:ss A", "HH:mm:ss"]).format("HH:mm:ss")
    : extractTime(spiritLogTime);
  const time = formattedDateAndTime(`${spiritLogDate} ${timeString}`);
  const d = moment(time);
  const timeWithYieldSecond = d.add(Math.abs(totalSeconds), "seconds");
  const duration = timeWithYieldSecond.format("YYYY-MM-DD HH:mm:ss");
  return moment
    .utc(duration, "YYYY-MM-DD HH:mm:ss")
    .local()
    .format("DD MMM YYYY hh:mm A");
};

export const convertSecondsToTime = (seconds) => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(secs.toFixed(0)).padStart(2, "0")}`;
};

export const showFormattedDateAndTime = (
  date,
  format = "YYYY-MM-DD hh:mm:ss A",
) => {
  if (!date) return "";
  const m = moment.utc(date, [
    "YYYY-MM-DDTHH:mm:ss.SSSSSSZ",
    "YYYY-MM-DDTHH:mm:ss.SSSZ",
    "YYYY-MM-DDTHH:mm:ss.SSSSSS",
    "YYYY-MM-DDTHH:mm:ss.SSS",
    "YYYY-MM-DDTHH:mm:ss",
    "YYYY-MM-DD HH:mm:ss",
    "YYYY-MM-DD",
    "MM-DD-YYYY",
    moment.ISO_8601,
  ]);
  if (m.isValid()) {
    return m.local().format(format);
  }
  const fallback = moment(date);
  return fallback.isValid() ? fallback.local().format(format) : "";
};